import { TutorialStep } from '../types'
import { selectors } from '../selectors'
import { createTabAutoClickHandler } from '../stepManagement/actionHelpers'
import { openRerunDialogManually } from '../stepManagement/actionHelpers'
import { waitForElementAndScroll } from '../stepManagement/elementUtils'

export const backtestSteps: readonly TutorialStep[] = [
  {
    target: 'body',
    section: 'Backtest Overview',
    disableBeacon: true,
    placement: 'center',
    title: 'Welcome to Backtests',
    content:
      'Backtests give you insights into how your trading strategy would have performed in the past.',
    isWelcomeScreen: true,
    video: 'backtestIntro',
  },
  {
    target: selectors.backtestName,
    placement: 'bottom',
    title: 'Backtest Name',
    content: 'The backtest name',
    section: 'Backtest Card',
    disableBeacon: true,
  },
  {
    target: selectors.backtestPatternName,
    placement: 'bottom',
    title: 'Pattern Name',
    content: 'The pattern backtested',
    section: 'Backtest Card',
    disableBeacon: true,
  },
  {
    target: selectors.backtestGrade,
    section: 'Backtest Card',
    placement: 'top',
    title: 'Grade',
    content:
      'View the backtests profit over time. Hover your cursor over the line-graph to view profit by risk/reward ratio.',
    disableBeacon: true,
  },
  {
    target: selectors.openSettingsButton,
    section: 'Backtest Card',
    placement: 'top',
    title: 'Open Settings',
    content: 'View detailed settings and assumptions of backtest.',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.backtestSideNavigationTabSettings),
    target: selectors.backtestSideNavigationTabSettings,
    placement: 'right',
    title: 'Settings',
    content:
      'View settings such as pattern, symbol, date, account settings, and trade exit settings.',
    section: 'Backtest Details',
    onEnter: createTabAutoClickHandler(selectors.backtestSideNavigationTabSettings),
    disableBeacon: true,
  },
  {
    target: selectors.backtestSideNavigationTabReports,
    placement: 'right',
    title: 'Reports',
    content: 'View the profit based on risk/reward and download a detailed trade history report.',
    section: 'Backtest Details',
    onEnter: createTabAutoClickHandler(selectors.backtestSideNavigationTabReports),
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.backtestSideNavigationTabPattern),
    target: selectors.backtestSideNavigationTabPattern,
    placement: 'left',
    title: 'Patterns',
    content: 'View the Buy/Sell rules of the pattern used in the backtest.',
    section: 'Backtest Details',
    onEnter: createTabAutoClickHandler(selectors.backtestSideNavigationTabPattern),
    disableBeacon: true,
    floaterProps: {
      options: {
        modifiers: [
          {
            name: 'arrow',
            options: {
              padding: 4,
            },
          },
        ],
      },
    },
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.createAlertBacktestButton),
    target: selectors.createAlertBacktestButton,
    placement: 'bottom',
    title: 'Alerts',
    content: 'Create an alert from the pattern used in the backtest.',
    section: 'Backtest Details',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.favoriteBacktestIconButton),
    target: selectors.favoriteBacktestIconButton,
    placement: 'bottom',
    title: 'Favoriting',
    content:
      'Add backtest to your favorite list. View all favorites using top filters next to search.',
    section: 'Backtest Details',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.shareBacktestIconButton),
    target: selectors.shareBacktestIconButton,
    placement: 'bottom',
    title: 'Sharing',
    content: 'Share the backtest with another member.',
    section: 'Backtest Details',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.viewBacktestButton),
    target: selectors.viewBacktestButton,
    placement: 'right',
    title: 'View',
    content: 'View full screen chart and profit summary.',
    section: 'Backtest Details',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.rerunBacktestButton),
    target: selectors.rerunBacktestButton,
    placement: 'right',
    title: 'Rerun',
    content: 'Edit and rerun the backtest.',
    section: 'Backtest Details',
    disableBeacon: true,
    floaterProps: {
      options: {
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              padding: 25,
            },
          },
          {
            name: 'flip',
            options: {},
          },
        ],
      },
    },
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.backtestRunDialog),
    target: selectors.backtestRunDialog,
    placement: 'bottom',
    title: 'Rerun Introduction',
    content:
      'Before rerunning a backtest, consider the main three sections: Pattern Strategy, Account Details, and Trade Exit.',
    section: 'Rerun Backtest',
    onEnter: openRerunDialogManually,
    disableBeacon: true,
    floaterProps: {
      options: {
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              padding: 25,
            },
          },
          {
            name: 'flip',
            options: {},
          },
        ],
      },
    },
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.backtestRunDialogCost),
    target: selectors.backtestRunDialogCost,
    placement: 'top',
    title: 'Backtest Cost',
    content:
      'Consider the cost in credits before running a backtest. Your credit allotment each month is dependent on your membership level. Extra credits can be purchased in the Marketplace.',
    section: 'Rerun Backtest',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.backtestRunDialogPlay),
    target: selectors.backtestRunDialogPlay,
    placement: 'top',
    title: 'Run Backtest',
    content:
      'Once you have started the backtest, return to the Backtests page to view the processing status. Once it completes, view the report by filtering by "newest".',
    section: 'Rerun Backtest',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.backtestTypeSelectMenu),
    target: selectors.backtestTypeSelectMenu,
    placement: 'bottom',
    title: 'Backtest Views',
    content:
      'Backtests ran by other members (Showcase), Backtests created by you, and your favorited Backtests.',
    section: 'View Filters',
    disableBeacon: true,
  },
  {
    target: selectors.backtestFiltersButton,
    placement: 'bottom',
    title: 'Backtest Filters',
    content: 'Use filters to narrow your backtest search.',
    section: 'View Filters',
    disableBeacon: true,
  },
  {
    target: 'body',
    placement: 'center',
    title: 'Rate',
    content:
      'Please rate this tutorial (1 - poor, 5 - exceptional). Please provide us details of your experience or suggestions on improving this tutorial.',
    section: 'Review',
    isFeedback: true,
    hideFooter: true,
    disableBeacon: true,
  },
]
